import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import GeoIPMap from './GeoIPMap';
import LiveStream from './LiveStream';

const S = {
  grid4:   { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 },
  grid2:   { display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 },
  panel:   { background: '#141720', border: '1px solid #ffffff14', borderRadius: 8, padding: '14px 16px' },
  mono:    { fontFamily: 'JetBrains Mono, monospace' },
  label:   { fontSize: 11, color: '#6b7280', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '.05em', marginBottom: 6 },
};

const SEV_COLOR = { CRITICAL:'#ff0040', HIGH:'#ff3b4e', MEDIUM:'#f59e0b', LOW:'#10b981' };

// Severity → attack-type mapping — mirrors ATTACK_SEVERITY in detection.py:827-834
const SEV_TYPES = {
  CRITICAL: ['Data-Exfiltration', 'C2-Communication'],
  HIGH:     ['Defense-Evasion', 'Privilege-Escalation', 'Lateral-Movement'],
  MEDIUM:   ['Reconnaissance', 'Persistence'],
  LOW:      ['Living-Off-The-Land'],
};

const ATTACK_COLORS = {
  'APT-Reconnaissance':      '#a78bfa',
  'APT-Lateral-Movement':    '#38bdf8',
  'APT-Persistence':         '#f472b6',
  'APT-Privilege-Escalation':'#fb923c',
  'APT-Defense-Evasion':     '#facc15',
  'APT-C2-Communication':    '#4ade80',
  'APT-Data-Exfiltration':   '#f87171',
  'APT-Living-Off-The-Land': '#e879f9',
};

function MetricCard({ label, value, sub, accent, live }) {
  return (
    <div style={{ ...S.panel, position:'relative', overflow:'hidden' }}>
      <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:accent, borderRadius:'2px 2px 0 0' }} />
      {live && (
        <div style={{ position:'absolute', top:8, right:10, width:6, height:6, borderRadius:'50%', background:'#10b981', boxShadow:'0 0 6px #10b981' }} />
      )}
      <div style={S.label}>{label}</div>
      <div style={{ fontSize:26, fontWeight:600, ...S.mono, color:accent, lineHeight:1, transition:'all 0.3s' }}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      <div style={{ fontSize:11, color:'#6b7280', marginTop:4 }}>{sub}</div>
    </div>
  );
}

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'#1a1e2a', border:'1px solid #ffffff22', borderRadius:6, padding:'6px 10px', fontSize:12, fontFamily:'JetBrains Mono, monospace', color:'#e8eaf0' }}>
      {payload.map((p, i) => <div key={i}>{p.name}: {(p.value || 0).toLocaleString()}</div>)}
    </div>
  );
}

export default function Dashboard({ metrics: polledMetrics, alerts: polledAlerts, onUpdate, onStreamStart, onStreamEnd }) {
  // ── Live stream state — overrides polled data while streaming ──
  const [liveMetrics, setLiveMetrics] = useState(null);
  const [liveAlerts,  setLiveAlerts]  = useState([]);
  const [isStreaming,  setIsStreaming]  = useState(false);

  // During streaming: show live data. After stream/upload completes: show polled (backend is source of truth).
  const metrics = liveMetrics || polledMetrics;
  const alerts  = liveAlerts.length ? liveAlerts : (polledAlerts || []);

  // Called by LiveStream on EVERY SSE tick (every 500ms)
  const handleLiveUpdate = useCallback((streamMetrics, streamAlerts) => {
    if (streamMetrics === null) {
      setLiveMetrics(null);
      setLiveAlerts([]);
      setIsStreaming(false);
      return;
    }
    setLiveMetrics(streamMetrics);
    setLiveAlerts(prev => {
      const merged = [...(streamAlerts || []), ...prev];
      const seen   = new Set();
      return merged.filter(a => {
        const key = `${a.src_ip||a.source_ip}-${a.timestamp}-${a.attack_type}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }).slice(0, 100);
    });
    const s = streamMetrics._stream?.status;
    const nowStreaming = s === 'running';
    setIsStreaming(nowStreaming);
    if (nowStreaming && onStreamStart) onStreamStart();
  }, [onStreamStart]);

  const handleStreamComplete = useCallback(async () => {
    console.log('[Dashboard] Stream complete — syncing backend...');
    setIsStreaming(false);

    // Notify App to immediately refresh /api/metrics and revert to slow poll
    if (onStreamEnd) await onStreamEnd();

    // Hold live data briefly so there's no blank flash, then let polled data take over
    setTimeout(() => {
      setLiveMetrics(null);
      setLiveAlerts([]);
    }, 1500);

    if (onUpdate) onUpdate();
  }, [onUpdate, onStreamEnd]);

  // ── Build chart data ──
  const hourlyData = Array.from({ length: 24 }, (_, h) => ({
    hour:    `${String(h).padStart(2,'0')}:00`,
    attacks: metrics?.hourly_distribution?.[h] ?? metrics?.hourly_distribution?.[String(h)] ?? 0,
  }));

  const sevData = metrics?.severity_distribution
    ? ['CRITICAL','HIGH','MEDIUM','LOW'].map(k => ({
        name:  k,
        value: metrics.severity_distribution[k] || 0,
      })).filter(d => d.value > 0)
    : [];

  const attackTypes = (metrics?.attack_types || []).slice(0, 6);
  const maxCount    = attackTypes[0]?.count || 1;

  const feed = alerts.slice(0, 25);

  return (
    <div style={{ display:'grid', gap:16 }}>

      {/* ── Metric cards ── */}
      <div style={S.grid4}>
        <MetricCard
          label="ALERTS TODAY"
          value={metrics?.total_attacks || 0}
          sub={`${(metrics?.total_attacks || 0).toLocaleString()} attacks detected`}
          accent="#ff3b4e"
          live={isStreaming}
        />
        <MetricCard
          label="CRITICAL & HIGH"
          value={(metrics?.severity_distribution?.HIGH || 0) + (metrics?.severity_distribution?.CRITICAL || 0)}
          sub="HIGH + CRITICAL alerts"
          accent="#f59e0b"
          live={isStreaming}
        />
        <MetricCard
          label="ATTACK RATIO"
          value={`${(((metrics?.total_attacks || 0) / Math.max((metrics?.total_attacks || 0) + (metrics?.total_normal || 0), 1)) * 100).toFixed(2)}%`}
          accent="#10b981"
          live={isStreaming}
        />
        <MetricCard
          label="INCIDENTS"
          value={metrics?.incidents || 0}
          sub="5-min clusters"
          accent="#3b82f6"
          live={isStreaming}
        />
      </div>

      {/* ─ Area chart + Attack vs Normal ── */}
      <div style={S.grid2}>
        <div style={S.panel}>
          <div style={{ fontSize:11, color:'#9ca3af', ...S.mono, letterSpacing:'.1em', marginBottom:12 }}>
            ALERTS OVER TIME (24H)
            {isStreaming && <span style={{ marginLeft:8, fontSize:9, color:'#3b82f6', ...S.mono }}>● LIVE</span>}
          </div>
          <ResponsiveContainer width="100%" height={180} minHeight={180}>
            <AreaChart data={hourlyData} margin={{ top:10, right:10, left:0, bottom:0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff0a" />
              <XAxis dataKey="hour" tick={{ fill:'#6b7280', fontSize:10 }} tickLine={false} />
              <YAxis tick={{ fill:'#6b7280', fontSize:10 }} tickLine={false} axisLine={false} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="attacks" name="Attacks" stroke="#ff3b4e" fill="#ff3b4e18" strokeWidth={2} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div style={S.panel}>
          <div style={{ fontSize:11, color:'#9ca3af', ...S.mono, letterSpacing:'.1em', marginBottom:16 }}>ATTACK vs NORMAL</div>
          <div style={{ display:'flex', flexDirection:'column', gap:14, padding:'8px 0' }}>
            {[
              { label:'Attack', value: metrics?.total_attacks || 0, color:'#ff3b4e' },
              { label:'Normal', value: metrics?.total_normal  || 0, color:'#10b981' },
            ].map(({ label, value, color }) => {
              const total = (metrics?.total_attacks || 0) + (metrics?.total_normal || 0);
              const pct   = total > 0 ? (value / total) * 100 : 0;
              return (
                <div key={label}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
                    <span style={{ fontSize:11, color, ...S.mono }}>{label}</span>
                    <span style={{ fontSize:11, color:'#9ca3af', ...S.mono }}>
                      {value.toLocaleString()} <span style={{ color:'#4b5563' }}>({pct.toFixed(2)}%)</span>
                    </span>
                  </div>
                  <div style={{ height:28, background:'#ffffff08', borderRadius:4, overflow:'hidden' }}>
                    <div style={{
                      width: `${Math.max(pct, 0.3)}%`, height:'100%',
                      background: color, borderRadius:4,
                      transition:'width 0.6s ease',
                      minWidth: value > 0 ? 4 : 0,
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop:12, padding:'8px 10px', background:'#ffffff05', borderRadius:5 }}>
            <div style={{ fontSize:10, color:'#6b7280', ...S.mono, textAlign:'center' }}>
              Total flows: <span style={{ color:'#e8eaf0' }}>
                {((metrics?.total_attacks || 0) + (metrics?.total_normal || 0)).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Severity + Top Attacks + Live Feed ── */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>

        <div style={S.panel}>
          <div style={{ fontSize:11, color:'#9ca3af', ...S.mono, letterSpacing:'.1em', marginBottom:12 }}>SEVERITY DISTRIBUTION</div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={sevData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff0a" />
              <XAxis dataKey="name" tick={{ fill:'#6b7280', fontSize:11 }} tickLine={false} />
              <YAxis tick={{ fill:'#6b7280', fontSize:10 }} tickLine={false} axisLine={false} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="value" radius={[4,4,0,0]} isAnimationActive={false}>
                {sevData.map((d, i) => (
                  <Cell key={i} fill={SEV_COLOR[d.name] || '#6b7280'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          {/* Severity → attack-type mapping — mirrors detection.py:827-834 */}
          <div style={{ marginTop:10, paddingTop:10, borderTop:'1px solid #ffffff14', ...S.mono, fontSize:10, lineHeight:1.7, color:'#9ca3af' }}>
            {Object.entries(SEV_TYPES).map(([sev, types]) => (
              <div key={sev} style={{ display:'flex', gap:8, marginBottom:2 }}>
                <span style={{ color:SEV_COLOR[sev], fontWeight:700, minWidth:62 }}>{sev}</span>
                <span style={{ color:'#6b7280' }}>{types.join(' · ')}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={S.panel}>
          <div style={{ fontSize:11, color:'#9ca3af', ...S.mono, letterSpacing:'.1em', marginBottom:12 }}>TOP ATTACK TYPES</div>
          <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
            {attackTypes.map(({ type, count }) => {
              const col = ATTACK_COLORS[type] || '#6366f1';
              return (
                <div key={type} style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontSize:10, color:'#6b7280', ...S.mono, minWidth:140, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {type.replace('APT-','')}
                  </span>
                  <div style={{ flex:1, height:4, background:'#ffffff08', borderRadius:2, overflow:'hidden' }}>
                    <div style={{ width:`${(count / maxCount) * 100}%`, height:'100%', background:col, borderRadius:2, transition:'width 0.4s ease' }} />
                  </div>
                  <span style={{ fontSize:10, color:col, ...S.mono, minWidth:42, textAlign:'right' }}>
                    {count.toLocaleString()}
                  </span>
                </div>
              );
            })}
            {!attackTypes.length && (
              <div style={{ color:'#374151', fontSize:11, ...S.mono, textAlign:'center', padding:20 }}>No data</div>
            )}
          </div>
        </div>

        <div style={S.panel}>
          <div style={{ fontSize:11, color:'#9ca3af', ...S.mono, letterSpacing:'.1em', marginBottom:10 }}>
            LIVE ALERT FEED
            {isStreaming && <span style={{ marginLeft:8, fontSize:9, color:'#10b981', ...S.mono }}>● LIVE</span>}
          </div>
          <div style={{ maxHeight:180, overflowY:'auto' }}>
            {feed.map((a, i) => {
              const sev   = (a.severity || a.threat_level || '').toUpperCase();
              const isAPT = a.prediction === 'APT';
              const col   = isAPT ? (SEV_COLOR[sev] || '#10b981') : '#10b981';
              const ip    = a.source_ip || a.src_ip || '?';
              return (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:8, padding:'5px 0', borderBottom:'1px solid #ffffff0a' }}>
                  <div style={{ width:6, height:6, borderRadius:'50%', background:col, flexShrink:0 }} />
                  <div style={{ flex:1, fontSize:11, ...S.mono, color:'#9ca3af', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    [{ip}] {a.attack_type}
                  </div>
                  <div style={{ fontSize:10, color:'#6b7280', ...S.mono }}>
                    {a.timestamp ? new Date(a.timestamp).toLocaleTimeString('en-US', { hour12:false }) : ''}
                  </div>
                </div>
              );
            })}
            {!feed.length && (
              <div style={{ color:'#374151', fontSize:11, ...S.mono, textAlign:'center', padding:20 }}>Awaiting data...</div>
            )}
          </div>
        </div>
      </div>

     {/* ── Smart City Threat Map ── */}
	 {/* <GeoIPMap alerts={alerts} /> */}

      {/* ── Live Stream panel ─ */}
      <LiveStream
        onLiveUpdate={handleLiveUpdate}
        onComplete={handleStreamComplete}
      />

    </div>
  );
}